<script setup>
import { useBackup } from '@/composables/useBackup'

// pages/administration/backup/index.vue — Module 23 (§32). Riwayat +
// tombol Backup Sekarang + upload untuk Restore.
definePage({
  meta: {
    layout: 'default',
    action: 'manage',
    subject: 'Backup',
  },
})

const { backups, loading, restoring, fetchBackups, downloadBackup, restoreBackup } = useBackup()

onMounted(fetchBackups)

const headers = [
  { title: 'File', key: 'filename' },
  { title: 'Ukuran', key: 'size_bytes' },
  { title: 'Status', key: 'status' },
  { title: 'Dipicu Oleh', key: 'triggered_by' },
  { title: 'Waktu', key: 'created_at' },
  { title: '', key: 'actions', sortable: false },
]

const formatBytes = bytes => {
  if (!bytes) return '-'
  const mb = bytes / 1024 / 1024

  return `${mb.toFixed(2)} MB`
}

const restoreDialog = ref(false)
const restoreFile = ref(null)
const restoreResult = ref(null)

const hasRestoreFile = computed(() => (Array.isArray(restoreFile.value) ? restoreFile.value.length > 0 : !!restoreFile.value))

const submitRestore = async () => {
  restoreResult.value = null
  try {
    const file = Array.isArray(restoreFile.value) ? restoreFile.value[0] : restoreFile.value

    await restoreBackup(file)
    restoreResult.value = { type: 'success', message: 'Restore berhasil. Restart Local Service secara manual untuk memuat ulang aplikasi.' }
  } catch (e) {
    restoreResult.value = { type: 'error', message: e?.data?.message || 'Restore gagal — arsip tidak valid atau rusak.' }
  }
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap gap-3 mb-4">
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">
          Backup &amp; Restore
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Riwayat backup — retensi otomatis (§32)
        </p>
      </div>
      <div class="d-flex gap-3">
        <BackupProgress @created="fetchBackups" />
        <VBtn
          variant="tonal"
          prepend-icon="ri-upload-2-line"
          @click="restoreDialog = true"
        >
          Restore dari Arsip
        </VBtn>
      </div>
    </div>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="backups"
        :loading="loading"
        hide-default-footer
      >
        <template #item.size_bytes="{ item }">
          {{ formatBytes(item.size_bytes) }}
        </template>
        <template #item.status="{ item }">
          <VChip
            size="small"
            :color="item.status === 'success' ? 'success' : 'error'"
            variant="tonal"
          >
            {{ item.status }}
          </VChip>
        </template>
        <template #item.triggered_by="{ item }">
          {{ item.triggered_by?.name ?? '-' }}
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="item.status === 'success'"
            size="small"
            variant="text"
            icon="ri-download-2-line"
            @click="downloadBackup(item)"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada riwayat backup.
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <VDialog
      v-model="restoreDialog"
      max-width="480"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          Restore dari Arsip Backup
        </VCardTitle>
        <VCardText class="pa-6">
          <VAlert
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            Restore akan menimpa database dan storage saat ini. Pastikan Anda punya backup terbaru sebelum melanjutkan.
          </VAlert>
          <VAlert
            v-if="restoreResult"
            :type="restoreResult.type"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ restoreResult.message }}
          </VAlert>
          <VFileInput
            v-model="restoreFile"
            label="File Arsip (.zip)"
            accept=".zip"
          />
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="restoring"
            @click="restoreDialog = false"
          >
            Tutup
          </VBtn>
          <VBtn
            color="error"
            :loading="restoring"
            :disabled="!hasRestoreFile"
            @click="submitRestore"
          >
            Restore
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
